/**
 * SettingsTab Component
 *
 * Settings tab for the profile page displaying user preferences and account actions.
 * Includes appearance settings (dark mode) and account management.
 */

import { useState, useContext, type CSSProperties } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { ThemeContext } from '@/contexts/ThemeContext';
import { Toggle } from '@/components/ui/Toggle';
import { AccountDeleteDialog } from '@/components/auth/AccountDeleteDialog';

/**
 * SettingsTab component for user preferences and account management
 */
export const SettingsTab = () => {
  const { user, signOut } = useAuth();
  const { memberships } = useGymMemberships();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Check if user owns any gyms (owners cannot delete their account)
  const ownedGyms = memberships.filter((m) => m.role === 'owner');
  const canDeleteAccount = ownedGyms.length === 0;

  /**
   * Format member since date
   */
  const memberSince = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      })
    : 'Unknown';

  return (
    <div style={containerStyle}>
      {/* Appearance Section */}
      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Appearance</h3>
        <div style={settingRowStyle}>
          <span style={settingLabelStyle}>Dark Mode</span>
          <Toggle checked={theme === 'dark'} onChange={toggleTheme} />
        </div>
      </section>

      {/* Account Section */}
      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Account</h3>
        <div style={infoRowStyle}>
          <span style={infoLabelStyle}>Email</span>
          <span style={infoValueStyle}>{user?.email}</span>
        </div>
        <div style={infoRowStyle}>
          <span style={infoLabelStyle}>Member since</span>
          <span style={infoValueStyle}>{memberSince}</span>
        </div>
      </section>

      {/* Actions Section */}
      <div style={actionsStyle}>
        <button style={signOutButtonStyle} onClick={signOut}>
          Sign Out
        </button>
        <button
          style={{
            ...deleteButtonStyle,
            ...(canDeleteAccount ? {} : disabledDeleteButtonStyle),
          }}
          onClick={() => setShowDeleteDialog(true)}
          disabled={!canDeleteAccount}
        >
          Delete Account
        </button>
        {!canDeleteAccount && (
          <p style={deleteWarningStyle}>
            You own {ownedGyms.length} gym{ownedGyms.length > 1 ? 's' : ''}. Transfer ownership
            before deleting your account.
          </p>
        )}
      </div>

      {/* Account Delete Dialog */}
      <AccountDeleteDialog isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} />
    </div>
  );
};

/**
 * Component styles using CSS variables for theming
 */
const containerStyle: CSSProperties = {
  padding: 'var(--spacing-md)',
};

const sectionStyle: CSSProperties = {
  marginBottom: 'var(--spacing-lg)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  marginBottom: 'var(--spacing-sm)',
};

const settingRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-md)',
  marginBottom: 'var(--spacing-sm)',
};

const settingLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
};

const infoRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: 'var(--spacing-sm) 0',
  borderBottom: '1px solid var(--color-border)',
};

const infoLabelStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
};

const infoValueStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
};

const actionsStyle: CSSProperties = {
  marginTop: 'var(--spacing-xl)',
};

const signOutButtonStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-md)',
  fontSize: 'var(--font-size-base)',
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  marginBottom: 'var(--spacing-sm)',
};

const deleteButtonStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-md)',
  fontSize: 'var(--font-size-base)',
  backgroundColor: 'transparent',
  color: 'var(--color-error)',
  border: '1px solid var(--color-error)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
};

const disabledDeleteButtonStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const deleteWarningStyle: CSSProperties = {
  marginTop: 'var(--spacing-sm)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
};
